import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { fileUrl, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import {
  ArrowLeft, Phone, MessageCircle, FileDown, Hammer, History, BellRing,
  NotebookPen, Calendar, Check, ChevronDown, FileText, Wrench, Save,
  Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** Prepend +91 country code for Indian numbers that don't already have it. */
function formatWhatsApp(phone) {
  const digits = (phone || "").replace(/[^0-9]/g, "");
  if (digits.startsWith("91") && digits.length >= 12) return digits;
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

const STATUSES = ["Pending", "Assigned", "In Progress", "Ready", "Delivered"];

const JEWELLERY_TYPES = [
  "Necklace", "Ring", "Earring", "Bangle", "Bracelet", "Pendant",
  "Chain", "Mangalsutra", "Nose Pin", "Anklet", "Kadli", "Other",
];
const METAL_TYPES = [
  "22 CT Gold",
  "18 CT Rose Gold",
  "18 CT Yellow Gold",
];

function FollowUpsTab({ order, reload }) {
  const [form, setForm] = useState({ method: "Call", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.notes.trim()) { toast.error("Add notes"); return; }
    try {
      await api.post(`/orders/${order.id}/followups`, form);
      setForm({ method: "Call", notes: "" });
      toast.success("Follow-up logged");
      reload();
    } catch { toast.error("Failed to log"); }
  };
  const phone = order.source_phone;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        <a href={`tel:${phone}`}>
          <Button variant="outline" data-testid="followup-call"><Phone className="w-4 h-4 mr-1.5" /> Call {phone}</Button>
        </a>
        <a href={`https://wa.me/${formatWhatsApp(phone)}`} target="_blank" rel="noreferrer">
          <Button variant="outline" className="text-emerald-600" data-testid="followup-whatsapp"><MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp</Button>
        </a>
      </div>

      <Card className="p-5">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[160px_1fr_auto] gap-3 items-start">
          <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
            <SelectTrigger data-testid="followup-method-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Call">Call</SelectItem>
              <SelectItem value="WhatsApp">WhatsApp</SelectItem>
              <SelectItem value="In-person">In-person</SelectItem>
            </SelectContent>
          </Select>
          <Textarea
            rows={2}
            placeholder="What did the customer say?"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="followup-notes-input"
          />
          <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800 md:self-stretch" data-testid="followup-submit">Log</Button>
        </form>
      </Card>

      <div>
        <h4 className="font-heading font-bold text-lg mb-3">History</h4>
        {(order.follow_ups || []).length === 0 ? (
          <Card className="p-8 text-center text-sm text-zinc-500">No follow-ups yet.</Card>
        ) : (
          <ol className="relative border-l border-border ml-3 space-y-4">
            {[...(order.follow_ups || [])].reverse().map((f) => (
              <li key={f.id} className="ml-6">
                <span className="absolute -left-1.5 mt-1.5 w-3 h-3 bg-zinc-900 rounded-full border-2 border-white" />
                <div className="text-xs text-zinc-500">{new Date(f.contacted_at).toLocaleString()}</div>
                <div className="font-medium text-sm">{f.method} · {f.by_name}</div>
                <div className="text-sm text-zinc-700 whitespace-pre-wrap">{f.notes}</div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function RemindersTab({ order, reload }) {
  const [form, setForm] = useState({ title: "", due_at: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.due_at) { toast.error("Title & date required"); return; }
    try {
      await api.post(`/orders/${order.id}/reminders`, {
        title: form.title,
        due_at: new Date(form.due_at).toISOString(),
      });
      setForm({ title: "", due_at: "" });
      toast.success("Reminder added");
      reload();
    } catch { toast.error("Failed"); }
  };
  const toggle = async (rid) => {
    try {
      await api.post(`/orders/${order.id}/reminders/${rid}/toggle`);
      reload();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-5">
      <Card className="p-5">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-3">
          <Input placeholder="What to remind?" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="reminder-title-input" />
          <Input type="datetime-local" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} data-testid="reminder-due-input" />
          <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" data-testid="reminder-submit">Add</Button>
        </form>
      </Card>
      {(order.reminders || []).length === 0 ? (
        <Card className="p-8 text-center text-sm text-zinc-500">No reminders.</Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <ul className="divide-y divide-border">
            {[...(order.reminders || [])].reverse().map((r) => (
              <li key={r.id} className={`flex items-center gap-3 p-4 ${r.done ? "opacity-50" : ""}`}>
                <button
                  onClick={() => toggle(r.id)}
                  className={`w-5 h-5 rounded border ${r.done ? "bg-zinc-900 border-zinc-900 text-white" : "border-zinc-300"} grid place-items-center`}
                >
                  {r.done && <Check className="w-3 h-3" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-sm ${r.done ? "line-through" : ""}`}>{r.title}</div>
                  <div className="text-xs text-zinc-500"><Calendar className="inline w-3 h-3 mr-1" />{new Date(r.due_at).toLocaleString()}</div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

const METALS = ["Gold", "Silver"];
const PURITIES = {
  Gold: ["9K", "14K", "18K", "22K"],
  Silver: ["925", "Silver Jewellery"],
};

function CraftingTab({ order, reload, employees = [] }) {
  const [form, setForm] = useState({
    jewellery_type: order.jewellery_type || "",
    metal_type: order.metal_type || "",
    purity: order.purity || "",
    size: order.size || "",
    size_dimensions: order.size_dimensions || "",
    finish_type: order.finish_type || "",
    karigar_notes: order.karigar_notes || "",
    estimated_weight: order.estimated_weight ?? 0,
    final_weight: order.final_weight ?? 0,
    description: order.description || "",
    modification_instructions: order.modification_instructions || "",
    title: order.title,
    customer_id: order.customer_id,
    source_phone: order.source_phone,
    expected_delivery: order.expected_delivery || null,
    priority: order.priority,
    image_paths: order.image_paths || [],
    sample_given: order.sample_given || false,
    sample_tag_number: order.sample_tag_number || "",
    sample_weight: order.sample_weight ?? 0,
    employee_ids: order.employee_ids || [],
    delivery_person_id: order.delivery_person_id || "",
    gold_rate_fixed: order.gold_rate_fixed || false,
    fixed_gold_rate: order.fixed_gold_rate ?? 0,
    advance_given: order.advance_given || false,
    advance_amount: order.advance_amount ?? 0,
    advance_metal_type: order.advance_metal_type || "",
    advance_metal_weight: order.advance_metal_weight ?? 0,
    booking_type: order.booking_type || "none",
    booked_rate: order.booked_rate ?? 0,
    booked_weight: order.booked_weight ?? 0,
    book_rate_only_for_paid_amount: order.book_rate_only_for_paid_amount || false,
  });

  useEffect(() => {
    setForm({
      jewellery_type: order.jewellery_type || "",
      metal_type: order.metal_type || "",
      purity: order.purity || "",
      size: order.size || "",
      size_dimensions: order.size_dimensions || "",
      finish_type: order.finish_type || "",
      karigar_notes: order.karigar_notes || "",
      estimated_weight: order.estimated_weight ?? 0,
      final_weight: order.final_weight ?? 0,
      description: order.description || "",
      modification_instructions: order.modification_instructions || "",
      title: order.title,
      customer_id: order.customer_id,
      source_phone: order.source_phone,
      expected_delivery: order.expected_delivery || null,
      priority: order.priority,
      image_paths: order.image_paths || [],
      sample_given: order.sample_given || false,
      sample_tag_number: order.sample_tag_number || "",
      sample_weight: order.sample_weight ?? 0,
      employee_ids: order.employee_ids || [],
      delivery_person_id: order.delivery_person_id || "",
      gold_rate_fixed: order.gold_rate_fixed || false,
      fixed_gold_rate: order.fixed_gold_rate ?? 0,
      advance_given: order.advance_given || false,
      advance_amount: order.advance_amount ?? 0,
      advance_metal_type: order.advance_metal_type || "",
      advance_metal_weight: order.advance_metal_weight ?? 0,
      booking_type: order.booking_type || "none",
      booked_rate: order.booked_rate ?? 0,
      booked_weight: order.booked_weight ?? 0,
      book_rate_only_for_paid_amount: order.book_rate_only_for_paid_amount || false,
    });
  }, [order]);

  // Reset purity if metal changes
  useEffect(() => {
    if (form.metal_type && form.metal_type !== order.metal_type) {
      setForm((f) => ({ ...f, purity: "" }));
    }
  }, [form.metal_type, order.metal_type]);

  const save = async () => {
    if (form.sample_given && (!form.sample_tag_number.trim() || !form.sample_weight)) {
      toast.error("Sample Tag Number and Sample Weight are required when Sample Given is toggled.");
      return;
    }
    if (form.gold_rate_fixed && !form.fixed_gold_rate) {
      toast.error("Please enter the fixed gold rate.");
      return;
    }
    if (form.advance_given && !form.advance_amount && (!form.advance_metal_type || form.advance_metal_type === "none" || !form.advance_metal_weight)) {
      toast.error("Please enter the advance amount or advance metal weight.");
      return;
    }

    try {
      await api.put(`/orders/${order.id}`, {
        ...form,
        estimated_weight: parseFloat(form.estimated_weight) || 0,
        final_weight: parseFloat(form.final_weight) || 0,
        sample_weight: parseFloat(form.sample_weight) || 0,
        fixed_gold_rate: parseFloat(form.fixed_gold_rate) || 0,
        advance_amount: parseFloat(form.advance_amount) || 0,
        advance_metal_type: form.advance_metal_type || "",
        advance_metal_weight: parseFloat(form.advance_metal_weight) || 0,
        booked_rate: parseFloat(form.booked_rate) || 0,
        booked_weight: parseFloat(form.booked_weight) || 0,
        delivery_person_id: form.delivery_person_id === "none" ? null : form.delivery_person_id || null,
      });
      toast.success("Crafting instructions saved");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Card className="p-6 bg-amber-50/40 border-amber-200 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h4 className="font-heading font-bold text-lg flex items-center gap-2">
            <Wrench className="w-4 h-4 text-amber-700" /> Crafting Instructions
          </h4>
          <p className="text-xs text-zinc-600 mt-1">Sent to karigar only. Appears on Karigar Copy.</p>
        </div>
        <Button onClick={save} className="bg-zinc-900 hover:bg-zinc-800" data-testid="crafting-save">
          <Save className="w-4 h-4 mr-1.5" /> Save Changes
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 border-b border-amber-200/50 pb-5">
        <div className="space-y-2">
          <Label>Jewellery Type</Label>
          <Select value={form.jewellery_type} onValueChange={(v) => setForm({ ...form, jewellery_type: v })}>
            <SelectTrigger data-testid="crafting-jewellery-type"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              {JEWELLERY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Metal</Label>
            <Select value={form.metal_type} onValueChange={(v) => setForm({ ...form, metal_type: v })}>
              <SelectTrigger data-testid="crafting-metal-select"><SelectValue placeholder="Metal" /></SelectTrigger>
              <SelectContent>
                {METALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Purity</Label>
            <Select disabled={!form.metal_type} value={form.purity} onValueChange={(v) => setForm({ ...form, purity: v })}>
              <SelectTrigger data-testid="crafting-purity-select"><SelectValue placeholder="Purity" /></SelectTrigger>
              <SelectContent>
                {form.metal_type && PURITIES[form.metal_type].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Approx Weight Guidance (g)</Label>
          <Input
            type="number" step="0.001"
            value={form.estimated_weight}
            onChange={(e) => setForm({ ...form, estimated_weight: e.target.value })}
            placeholder="0.000"
            data-testid="crafting-approx-weight"
          />
        </div>
        <div className="space-y-2">
          <Label>Final Weight (g)</Label>
          <Input
            type="number" step="0.001"
            value={form.final_weight}
            onChange={(e) => setForm({ ...form, final_weight: e.target.value })}
            placeholder="Fill when delivered"
            data-testid="crafting-final-weight"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Size (e.g. Ring size)</Label>
            <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="e.g. 14" data-testid="crafting-size-input" />
          </div>
          <div className="space-y-2">
            <Label>Dimensions / Length</Label>
            <Input value={form.size_dimensions} onChange={(e) => setForm({ ...form, size_dimensions: e.target.value })} data-testid="crafting-size-dim" />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Finish Type</Label>
          <Input value={form.finish_type} onChange={(e) => setForm({ ...form, finish_type: e.target.value })} data-testid="crafting-finish" />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label>Modification Details</Label>
          <Textarea
            rows={3}
            value={form.modification_instructions}
            onChange={(e) => setForm({ ...form, modification_instructions: e.target.value })}
            data-testid="crafting-modification"
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label>Karigar Notes / Special Instructions</Label>
          <Textarea
            rows={3}
            value={form.karigar_notes}
            onChange={(e) => setForm({ ...form, karigar_notes: e.target.value })}
            placeholder="Internal notes for karigar — not shown to customer"
            data-testid="crafting-karigar-notes"
          />
        </div>

        {/* Sample Given toggle & fields */}
        <div className="md:col-span-2 border-t border-amber-200/50 pt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-semibold">Is Sample Given?</Label>
              <p className="text-xs text-zinc-500">Toggle if the customer provided a physical sample piece.</p>
            </div>
            <Switch
              checked={form.sample_given}
              onCheckedChange={(checked) => setForm({ ...form, sample_given: checked })}
              data-testid="crafting-sample-switch"
            />
          </div>

          {form.sample_given && (
            <div className="grid grid-cols-2 gap-4 p-3 bg-amber-50/50 rounded-lg border border-amber-100 animate-in fade-in duration-200">
              <div className="space-y-2">
                <Label>Sample Tag Number *</Label>
                <Input
                  required
                  value={form.sample_tag_number}
                  onChange={(e) => setForm({ ...form, sample_tag_number: e.target.value })}
                  placeholder="e.g. TAG-983"
                  data-testid="crafting-sample-tag"
                />
              </div>
              <div className="space-y-2">
                <Label>Sample Weight (g) *</Label>
                <Input
                  required
                  type="number" step="0.001"
                  value={form.sample_weight}
                  onChange={(e) => setForm({ ...form, sample_weight: e.target.value })}
                  placeholder="0.000"
                  data-testid="crafting-sample-weight"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Staff Assignment & Delivery */}
      <div className="space-y-4 border-b border-amber-200/50 pb-5">
        <div>
          <h4 className="text-md font-heading font-bold text-zinc-800">Staff & Scheduling</h4>
          <p className="text-xs text-zinc-500 mt-0.5">Assign internal staff members for order handling and dispatch.</p>
        </div>
        <div className="space-y-2.5">
          <Label className="font-semibold">Assigned Employees</Label>
          {employees.length === 0 ? (
            <p className="text-xs text-zinc-400">No active employees available.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {employees.map((emp) => (
                <label key={emp.id} className="flex items-center gap-2.5 text-sm cursor-pointer p-2 rounded-md border border-zinc-200 hover:bg-zinc-50 transition-colors">
                  <Checkbox
                    checked={form.employee_ids.includes(emp.id)}
                    onCheckedChange={(checked) => {
                      setForm((f) => ({
                        ...f,
                        employee_ids: checked
                          ? [...f.employee_ids, emp.id]
                          : f.employee_ids.filter((id) => id !== emp.id),
                      }));
                    }}
                  />
                  <span>{emp.name} <span className="text-xs text-zinc-400">({emp.role})</span></span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2 max-w-sm">
          <Label>Delivery Person</Label>
          <Select value={form.delivery_person_id || "none"} onValueChange={(v) => setForm({ ...form, delivery_person_id: v })}>
            <SelectTrigger data-testid="crafting-delivery-person-select">
              <SelectValue placeholder="Assign delivery person" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassigned / Self</SelectItem>
              {employees.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.role})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pricing, Locking & Advance Booking */}
      <div className="space-y-5">
        <div>
          <h4 className="text-md font-heading font-bold text-zinc-800">Rate Locking & Booking Ledger</h4>
          <p className="text-xs text-zinc-500 mt-0.5">Edit gold rate locks and advance booking details associated with the order.</p>
        </div>

        <div className="space-y-5">
          {/* Gold Rate Fixed Toggle */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold">Fix Gold Rate?</Label>
                <p className="text-xs text-zinc-500">Locks the metal rate for this order. Required 100% advance coverage.</p>
              </div>
              <Switch
                checked={form.gold_rate_fixed}
                onCheckedChange={(checked) => setForm({ ...form, gold_rate_fixed: checked })}
                data-testid="crafting-rate-fixed-switch"
              />
            </div>

            {form.gold_rate_fixed && (
              <div className="max-w-xs p-3 bg-zinc-100/50 rounded border border-zinc-200 animate-in fade-in duration-200">
                <Label>Fixed Gold Rate (₹/g) *</Label>
                <Input
                  required
                  type="number"
                  value={form.fixed_gold_rate}
                  onChange={(e) => setForm({ ...form, fixed_gold_rate: e.target.value })}
                  placeholder="Rate per gram"
                  data-testid="crafting-fixed-rate-input"
                />
              </div>
            )}
          </div>

          {/* Advance Given Toggle */}
          <div className="border-t border-amber-200/50 pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold">Advance Payment Received?</Label>
                <p className="text-xs text-zinc-500">Enables upfront deposit recording in customer payment history.</p>
              </div>
              <Switch
                checked={form.advance_given}
                onCheckedChange={(checked) => setForm({ ...form, advance_given: checked })}
                data-testid="crafting-advance-given-switch"
              />
            </div>

            {form.advance_given && (
              <div className="space-y-4 p-4 bg-zinc-100/50 rounded-lg border border-zinc-200 animate-in fade-in duration-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Advance Amount (₹)</Label>
                    <Input
                      type="number"
                      value={form.advance_amount}
                      onChange={(e) => setForm({ ...form, advance_amount: e.target.value })}
                      placeholder="Amount in ₹"
                      data-testid="crafting-advance-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Advance Metal</Label>
                    <Select value={form.advance_metal_type} onValueChange={(v) => setForm({ ...form, advance_metal_type: v })}>
                      <SelectTrigger data-testid="crafting-advance-metal-type"><SelectValue placeholder="Select metal" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="Gold">Gold</SelectItem>
                        <SelectItem value="Silver">Silver</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Metal Weight (g)</Label>
                    <Input
                      type="number" step="0.001"
                      value={form.advance_metal_weight}
                      onChange={(e) => setForm({ ...form, advance_metal_weight: e.target.value })}
                      placeholder="0.000"
                      data-testid="crafting-advance-metal-weight"
                      disabled={!form.advance_metal_type || form.advance_metal_type === "none"}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Purity Percentage (%)</Label>
                    <Input
                      type="number" step="0.1"
                      value={form.advance_purity_percent || 100}
                      onChange={(e) => setForm({ ...form, advance_purity_percent: e.target.value })}
                      placeholder="100.0"
                      data-testid="crafting-advance-purity-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Advance Booking Type</Label>
                    <Select value={form.booking_type} onValueChange={(v) => setForm({ ...form, booking_type: v })}>
                      <SelectTrigger data-testid="crafting-booking-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Plain Payment (No Lock)</SelectItem>
                        <SelectItem value="rate">Rate Booking (Locks rate only for value paid)</SelectItem>
                        <SelectItem value="weight">Weight Booking (Locks rate for selected weight)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {form.booking_type !== "none" && (
                  <div className="grid grid-cols-2 gap-4 border-t border-zinc-200/80 pt-3 animate-in fade-in duration-150">
                    <div className="space-y-2">
                      <Label>Booked Rate (₹/g) *</Label>
                      <Input
                        required
                        type="number"
                        value={form.booked_rate}
                        onChange={(e) => setForm({ ...form, booked_rate: e.target.value })}
                        placeholder="Booking metal rate"
                        data-testid="crafting-booked-rate-input"
                      />
                    </div>
                    
                    {form.booking_type === "weight" ? (
                      <div className="space-y-2">
                        <Label>Booked Weight (g) *</Label>
                        <Input
                          required
                          type="number" step="0.001"
                          value={form.booked_weight}
                          onChange={(e) => setForm({ ...form, booked_weight: e.target.value })}
                          placeholder="Locked weight"
                          data-testid="crafting-booked-weight-input"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 pt-8">
                        <Checkbox
                          id="crafting-rate-limit"
                          checked={form.book_rate_only_for_paid_amount}
                          onCheckedChange={(checked) => setForm({ ...form, book_rate_only_for_paid_amount: !!checked })}
                        />
                        <Label htmlFor="crafting-rate-limit" className="text-xs cursor-pointer">
                          Book rate only for amount paid (remaining weight at delivery rate)
                        </Label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function RevisionsTab({ order, reload }) {
  const [text, setText] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/orders/${order.id}/revisions`, { instructions: text });
      setText("");
      toast.success("Revision added");
      reload();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-5">
      <Card className="p-5">
        <form onSubmit={submit} className="space-y-3">
          <Label>New Revision Instructions</Label>
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Describe what changed in this version…" data-testid="revision-input" />
          <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" data-testid="revision-submit">Add Revision</Button>
        </form>
      </Card>
      {(order.revisions || []).length === 0 ? (
        <Card className="p-8 text-center text-sm text-zinc-500">No revisions yet.</Card>
      ) : (
        <ol className="relative border-l border-border ml-3 space-y-4">
          {[...(order.revisions || [])].reverse().map((r) => (
            <li key={r.id} className="ml-6">
              <span className="absolute -left-1.5 mt-1.5 w-3 h-3 bg-gold-400 rounded-full border-2 border-white" />
              <div className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleString()} · {r.by_name}</div>
              <div className="font-mono text-sm font-bold">{r.version}</div>
              <div className="text-sm text-zinc-700 whitespace-pre-wrap">{r.instructions}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function NotesTab({ order, reload }) {
  const [text, setText] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/orders/${order.id}/notes`, { text });
      setText("");
      reload();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-5">
      <Card className="p-5">
        <form onSubmit={submit} className="space-y-3">
          <Label>Internal Note</Label>
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Visible to staff only…" data-testid="note-input" />
          <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" data-testid="note-submit">Add Note</Button>
        </form>
      </Card>
      {(order.notes || []).length === 0 ? (
        <Card className="p-8 text-center text-sm text-zinc-500">No notes.</Card>
      ) : (
        <div className="space-y-3">
          {[...(order.notes || [])].reverse().map((n) => (
            <Card key={n.id} className="p-4">
              <div className="text-xs text-zinc-500">{new Date(n.created_at).toLocaleString()} · {n.by_name}</div>
              <div className="text-sm text-zinc-800 whitespace-pre-wrap mt-1">{n.text}</div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineList({ entries }) {
  if (!entries || entries.length === 0) return <div className="text-sm text-zinc-500">No timeline events yet.</div>;
  return (
    <ol className="relative border-l border-border ml-3 space-y-4">
      {[...entries].reverse().map((t, i) => (
        <li key={i} className="ml-6">
          <span className="absolute -left-1.5 mt-1.5 w-3 h-3 bg-zinc-900 rounded-full border-2 border-white" />
          <div className="text-xs text-zinc-500">{new Date(t.at).toLocaleString()}</div>
          <div className="text-sm font-medium">{t.event}</div>
          <div className="text-xs text-zinc-500">{t.by_name}</div>
        </li>
      ))}
    </ol>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [karigars, setKarigars] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showDeleteOrder, setShowDeleteOrder] = useState(false);
  const [removeImagePath, setRemoveImagePath] = useState(null);

  const reload = useCallback(async () => {
    const { data } = await api.get(`/orders/${id}`);
    setOrder(data);
  }, [id]);

  useEffect(() => {
    reload();
    api.get("/karigars").then((r) => setKarigars(r.data));
    api.get("/employees?active=true").then((r) => setEmployees(r.data));
  }, [id, reload]);

  if (!order) return <div className="text-zinc-500">Loading…</div>;

  const updateStatus = async (status) => {
    try {
      await api.post(`/orders/${order.id}/status`, { status });
      toast.success(`Status: ${status}`);
      reload();
    } catch { toast.error("Failed"); }
  };
  const assignKarigar = async (karigar_id) => {
    try {
      await api.post(`/orders/${order.id}/assign`, { karigar_id: karigar_id || null });
      toast.success(karigar_id ? "Karigar assigned" : "Karigar unassigned");
      reload();
    } catch { toast.error("Failed"); }
  };

  const removeImage = (path) => {
    setRemoveImagePath(path);
  };

  const performRemoveImage = async () => {
    const path = removeImagePath;
    setRemoveImagePath(null);
    const next = (order.image_paths || []).filter((p) => p !== path);
    try {
      // 1. Physically delete the file from Cloudinary and DB
      await api.delete("/files", { params: { url: path } });

      // 2. Update the order with ALL current fields to prevent overwriting data
      await api.put(`/orders/${order.id}`, {
        customer_id: order.customer_id,
        title: order.title,
        description: order.description || "",
        modification_instructions: order.modification_instructions || "",
        source_phone: order.source_phone,
        expected_delivery: order.expected_delivery || null,
        priority: order.priority,
        image_paths: next,
        jewellery_type: order.jewellery_type || "",
        metal_type: order.metal_type || "",
        purity: order.purity || "",
        size: order.size || "",
        size_dimensions: order.size_dimensions || "",
        finish_type: order.finish_type || "",
        karigar_notes: order.karigar_notes || "",
        estimated_weight: order.estimated_weight || 0,
        final_weight: order.final_weight || 0,
        sample_given: order.sample_given || false,
        sample_tag_number: order.sample_tag_number || "",
        sample_weight: order.sample_weight || 0,
        employee_ids: order.employee_ids || [],
        delivery_person_id: order.delivery_person_id || null,
        gold_rate_fixed: order.gold_rate_fixed || false,
        fixed_gold_rate: order.fixed_gold_rate || 0,
        advance_given: order.advance_given || false,
        advance_amount: order.advance_amount || 0,
        advance_metal_type: order.advance_metal_type || "",
        advance_metal_weight: order.advance_metal_weight || 0,
        advance_purity_percent: order.advance_purity_percent || 100,
        booking_type: order.booking_type || "none",
        booked_rate: order.booked_rate || 0,
        booked_weight: order.booked_weight || 0,
        book_rate_only_for_paid_amount: order.book_rate_only_for_paid_amount || false,
      });
      toast.success("Image removed");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove image");
    }
  };

  const deleteOrder = async () => {
    try {
      await api.delete(`/orders/${order.id}`);
      toast.success("Order deleted");
      navigate("/orders");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete order");
    } finally {
      setShowDeleteOrder(false);
    }
  };

  const downloadJobSheet = async (variant) => {
    try {
      const r = await api.get(`/orders/${order.id}/jobsheet/${variant}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const fname = `jobsheet-${variant}-${order.order_no}.pdf`;
      // Use anchor-download — works on Safari, Android, and all desktop browsers
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a short delay to ensure the download has started
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch {
      toast.error("Could not generate job sheet");
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/orders" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Orders
      </Link>

      {/* Header — clarity block */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-zinc-500 break-all">{order.order_no}</span>
            <StatusBadge status={order.status} />
            <PriorityBadge priority={order.priority} />
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-2 break-words" data-testid="order-title">
            {order.title}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-600">
            {(order.jewellery_type || order.metal_type) && (
              <span className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-900 px-2 py-0.5 rounded-full text-xs font-semibold">
                {order.jewellery_type && <span>{order.jewellery_type}</span>}
                {order.jewellery_type && order.metal_type && <span className="opacity-50">·</span>}
                {order.metal_type && (
                  <span data-testid="order-metal-chip">
                    {order.purity ? `${order.purity} ${order.metal_type}` : order.metal_type}
                  </span>
                )}
              </span>
            )}
            <span>
              <span className="text-zinc-400">Customer · </span>
              <Link to={`/customers/${order.customer_id}`} className="font-medium hover:underline break-all" data-testid="order-customer-link">
                {order.customer_name}
              </Link>
            </span>
            {order.expected_delivery && (
              <span data-testid="order-delivery-date">
                <span className="text-zinc-400">Delivery · </span>
                <span className="font-medium">{new Date(order.expected_delivery).toLocaleDateString()}</span>
              </span>
            )}
            {order.karigar_name && (
              <span>
                <span className="text-zinc-400">Karigar · </span>
                <span className="font-medium">{order.karigar_name}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`tel:${order.source_phone}`}>
            <Button variant="outline" size="sm"><Phone className="w-3.5 h-3.5 mr-1" /> Call</Button>
          </a>
          <a href={`https://wa.me/${formatWhatsApp(order.source_phone)}`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="text-emerald-600">
              <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
            </Button>
          </a>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-zinc-900 hover:bg-zinc-800" size="sm" data-testid="jobsheet-trigger">
                <FileText className="w-3.5 h-3.5 mr-1" /> Generate Job Sheet
                <ChevronDown className="w-3.5 h-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => downloadJobSheet("customer")} data-testid="jobsheet-customer">
                <FileDown className="w-4 h-4 mr-2" /> Customer Copy
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadJobSheet("karigar")} data-testid="jobsheet-karigar">
                <FileDown className="w-4 h-4 mr-2" /> Karigar Copy
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {user?.role === "admin" && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              onClick={() => setShowDeleteOrder(true)}
              data-testid="delete-order-button"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={showDeleteOrder} onOpenChange={setShowDeleteOrder}>
        <AlertDialogContent data-testid="delete-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete order <span className="font-mono font-semibold">{order.order_no}</span>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-order-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={deleteOrder}
              data-testid="delete-order-confirm"
            >
              Delete order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image removal confirmation dialog */}
      <AlertDialog open={!!removeImagePath} onOpenChange={(o) => !o && setRemoveImagePath(null)}>
        <AlertDialogContent data-testid="remove-image-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this image?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the image from this order. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-image-cancel">Keep Image</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={performRemoveImage}
              data-testid="remove-image-confirm"
            >
              Remove Image
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status / assignment row */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="overline text-zinc-500 mb-2">Status</div>
          <Select value={order.status} onValueChange={updateStatus}>
            <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Card>
        <Card className="p-4">
          <div className="overline text-zinc-500 mb-2 flex items-center gap-1.5"><Hammer className="w-3 h-3" /> Karigar</div>
          <Select
            value={order.karigar_id || "__none__"}
            onValueChange={(v) => assignKarigar(v === "__none__" ? null : v)}
          >
            <SelectTrigger data-testid="karigar-select"><SelectValue placeholder="Assign karigar" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Unassigned —</SelectItem>
              {karigars.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Card>
      </div>

      {/* Reference Images */}
      {order.image_paths?.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-heading font-bold text-lg">Reference Images</h4>
            <span className="overline text-zinc-500">{order.image_paths.length}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="order-images">
            {order.image_paths.map((p) => (
              <div key={p} className="relative group aspect-square rounded-md overflow-hidden border border-border bg-zinc-100">
                <a href={fileUrl(p)} target="_blank" rel="noreferrer" className="block w-full h-full hover:opacity-90">
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                </a>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); removeImage(p); }}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-zinc-900/85 text-white grid place-items-center opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-opacity"
                  title="Remove image"
                  data-testid="remove-image-button"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="flex w-full overflow-x-auto justify-start">
          <TabsTrigger value="overview" data-testid="tab-overview"><History className="w-3.5 h-3.5 mr-1.5" />Overview</TabsTrigger>
          <TabsTrigger value="crafting" data-testid="tab-crafting"><Wrench className="w-3.5 h-3.5 mr-1.5" />Crafting</TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups"><MessageCircle className="w-3.5 h-3.5 mr-1.5" />Follow-ups</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders"><BellRing className="w-3.5 h-3.5 mr-1.5" />Reminders</TabsTrigger>
          <TabsTrigger value="revisions" data-testid="tab-revisions">Revisions</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes"><NotebookPen className="w-3.5 h-3.5 mr-1.5" />Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="pt-6 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="p-5 flex flex-col justify-between">
              <div>
                <h4 className="font-heading font-bold text-lg mb-3">Description</h4>
                <p className="text-sm whitespace-pre-wrap text-zinc-700">{order.description || "—"}</p>
                {order.modification_instructions && (
                  <>
                    <h4 className="font-heading font-bold text-lg mt-5 mb-2">Modification Details</h4>
                    <p className="text-sm whitespace-pre-wrap text-zinc-700 bg-amber-50 p-3 rounded-md border border-amber-200">{order.modification_instructions}</p>
                  </>
                )}
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3 text-sm border-t pt-3">
                <div><div className="overline text-zinc-500">Source Phone</div><div className="num font-semibold">{order.source_phone}</div></div>
                <div><div className="overline text-zinc-500">Created</div><div className="font-semibold">{new Date(order.created_at).toLocaleDateString()}</div></div>
              </div>
            </Card>

            <Card className="p-5 space-y-5">
              <div>
                <h4 className="font-heading font-bold text-lg mb-3">Specifications</h4>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                  <div>
                    <span className="overline text-zinc-500 block">Metal / Purity</span>
                    <span className="font-medium text-zinc-800">
                      {order.purity ? `${order.purity} ${order.metal_type}` : order.metal_type || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="overline text-zinc-500 block">Dedicated Size</span>
                    <span className="font-medium text-zinc-800">{order.size || "—"}</span>
                  </div>
                  <div>
                    <span className="overline text-zinc-500 block">Estimated Weight</span>
                    <span className="font-medium text-zinc-800">
                      {order.estimated_weight ? `${order.estimated_weight.toFixed(3)} g` : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="overline text-zinc-500 block">Final Weight</span>
                    <span className="font-medium text-zinc-800">
                      {order.final_weight ? `${order.final_weight.toFixed(3)} g` : "—"}
                    </span>
                  </div>
                  <div className="col-span-2 border-t border-zinc-100 pt-2">
                    <span className="overline text-zinc-500 block">Sample Status</span>
                    <span className="font-medium text-zinc-800">
                      {order.sample_given ? (
                        <span className="inline-block mt-0.5 text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 text-xs font-semibold">
                          Sample (Tag: {order.sample_tag_number} · {order.sample_weight?.toFixed(3)} g)
                        </span>
                      ) : (
                        <span className="text-zinc-400 text-xs">No Sample Provided</span>
                      )}
                    </span>
                  </div>
                  <div className="col-span-2 border-t border-zinc-100 pt-2">
                    <span className="overline text-zinc-500 block">Assigned Staff</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {order.employee_names?.length > 0 ? (
                        order.employee_names.map((name) => (
                          <span key={name} className="bg-zinc-100 px-1.5 py-0.5 rounded text-xs text-zinc-800 border border-zinc-200">
                            {name}
                          </span>
                        ))
                      ) : (
                        <span className="text-zinc-400 text-xs">Unassigned</span>
                      )}
                    </div>
                  </div>
                  <div className="col-span-2 border-t border-zinc-100 pt-2">
                    <span className="overline text-zinc-500 block">Delivery Person</span>
                    <span className="font-medium text-zinc-800 text-xs">{order.delivery_person_name || "Unassigned"}</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-zinc-200 pt-4">
                <h4 className="font-heading font-bold text-lg mb-3">Locking & Booking Ledger</h4>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                  <div>
                    <span className="overline text-zinc-500 block">Gold Rate Lock</span>
                    <span className="font-medium text-zinc-800">
                      {order.gold_rate_fixed ? (
                        <span className="inline-block mt-0.5 text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200 text-xs font-semibold">
                          Locked @ ₹{order.fixed_gold_rate?.toLocaleString()}/g
                        </span>
                      ) : (
                        <span className="text-zinc-500 text-xs">Market Rate</span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="overline text-zinc-500 block">Advance Deposit</span>
                    <span className="font-medium text-zinc-800">
                      {order.advance_given ? `₹${order.advance_amount?.toLocaleString()}` : "No Deposit"}
                    </span>
                  </div>
                  {order.advance_given && order.booking_type !== "none" && (
                    <div className="col-span-2 border-t border-zinc-100 pt-2">
                      <span className="overline text-zinc-500 block">Booking Mode</span>
                      <span className="font-medium text-zinc-800 text-xs capitalize">
                        {order.booking_type} Booking
                        {order.booking_type === "rate" && (
                          <span className="text-zinc-500 text-[11px] block mt-0.5 font-normal">
                            Locked rate ₹{order.booked_rate?.toLocaleString()}/g
                            {order.book_rate_only_for_paid_amount ? " for paid amount only" : " for entire order"}
                          </span>
                        )}
                        {order.booking_type === "weight" && (
                          <span className="text-zinc-500 text-[11px] block mt-0.5 font-normal">
                            Locked rate ₹{order.booked_rate?.toLocaleString()}/g for {order.booked_weight} g
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            <Card className="p-5">
              <h4 className="font-heading font-bold text-lg mb-3">Timeline</h4>
              <TimelineList entries={order.timeline} />
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="crafting" className="pt-6"><CraftingTab order={order} reload={reload} employees={employees} /></TabsContent>
        <TabsContent value="followups" className="pt-6"><FollowUpsTab order={order} reload={reload} /></TabsContent>
        <TabsContent value="reminders" className="pt-6"><RemindersTab order={order} reload={reload} /></TabsContent>
        <TabsContent value="revisions" className="pt-6"><RevisionsTab order={order} reload={reload} /></TabsContent>
        <TabsContent value="notes" className="pt-6"><NotesTab order={order} reload={reload} /></TabsContent>
      </Tabs>
    </div>
  );
}
